import { wmsApi } from '../../../config/apiClient';
import { ACCOUNTING_API } from '../../../config/api';

export interface CreateWmsOrderDto {
  WarehouseId: string;
  OrderNumber?: string;
  SourceType?: string;
  CustomerName: string;
  CustomerPhone?: string;
  CustomerAddress?: string;
  ShippingMethod?: string;
  Priority?: number;
  Items: CreateWmsOrderItemDto[];
}

export interface CreateWmsOrderItemDto {
  ProductId: string;
  Quantity: number;
  SKU?: string;
  Barcode?: string;
}

export interface UpdateWmsOrderDto {
  Id: string;
  CustomerName: string;
  CustomerPhone?: string;
  CustomerAddress?: string;
  ShippingMethod?: string;
  Priority?: number;
}

export interface OrderListParams {
  page?: number;
  pageSize?: number;
  status?: string;
  orderType?: string;
  search?: string;
  cancelHandlingStatus?: string;
  /** true: yalnızca İptal Yönetimi kuyruğundaki kayıtlar (eski havuz iptalleri hariç) */
  cancelQueueOnly?: boolean;
  /** İptal tarihi aralığı (yyyy-MM-dd) */
  cancelledFrom?: string;
  cancelledTo?: string;
  cancelSource?: string;
  sortBy?: string;
  sortDir?: string;
}

export type WmsCancelReasonCode =
  | 'stock_missing'
  | 'stock_mismatch'
  | 'damaged'
  | 'wrong_product'
  | 'barcode_issue'
  | 'other';

export interface CancelWmsOrderDto {
  ReasonCode: WmsCancelReasonCode;
  ReasonText?: string;
  Note?: string;
  Source?: 'Picking' | 'Pool' | 'Manual';
  CancelledById?: string;
  CancelledByName?: string;
}

export interface ResolveWmsCancelDto {
  OrderId: string;
  HandledByName?: string;
  Note?: string;
}

export const WMS_CANCEL_REASON_CODES: WmsCancelReasonCode[] = [
  'stock_missing',
  'stock_mismatch',
  'damaged',
  'wrong_product',
  'barcode_issue',
  'other',
];

export interface AssignOrderPersonnelDto {
  OrderId: string;
  PersonnelId: string;
  PersonnelName: string;
}

export interface UnassignOrderPersonnelDto {
  OrderId: string;
}

export const orderService = {
  getByWarehouse: (warehouseId: string, params?: OrderListParams) => {
    let url = ACCOUNTING_API.WMS_ORDERS(warehouseId);
    if (params) {
      const qp = new URLSearchParams();
      if (params.page) qp.append('page', params.page.toString());
      if (params.pageSize) qp.append('pageSize', params.pageSize.toString());
      if (params.status) qp.append('status', params.status);
      if (params.orderType) qp.append('orderType', params.orderType);
      if (params.search) qp.append('search', params.search);
      if (params.cancelHandlingStatus) qp.append('cancelHandlingStatus', params.cancelHandlingStatus);
      if (params.cancelQueueOnly) qp.append('cancelQueueOnly', 'true');
      if (params.cancelledFrom) qp.append('cancelledFrom', params.cancelledFrom);
      if (params.cancelledTo) qp.append('cancelledTo', params.cancelledTo);
      if (params.cancelSource) qp.append('cancelSource', params.cancelSource);
      if (params.sortBy) qp.append('sortBy', params.sortBy);
      if (params.sortDir) qp.append('sortDir', params.sortDir);
      const qs = qp.toString();
      if (qs) url += `?${qs}`;
    }
    return wmsApi.get(url);
  },

  getDetail: (id: string) =>
    wmsApi.get(ACCOUNTING_API.WMS_ORDER_DETAIL(id)),

  create: (data: CreateWmsOrderDto) =>
    wmsApi.post(ACCOUNTING_API.WMS_ORDER_CREATE, data),

  update: (data: UpdateWmsOrderDto) =>
    wmsApi.post(ACCOUNTING_API.WMS_ORDER_UPDATE, data),

  delete: (id: string) =>
    wmsApi.post(ACCOUNTING_API.WMS_ORDER_DELETE, { Id: id }),

  cancel: (id: string, data?: CancelWmsOrderDto) =>
    wmsApi.post(ACCOUNTING_API.WMS_ORDER_CANCEL(id), data || { ReasonCode: 'other', Source: 'Manual' }),

  resolveCancel: (data: ResolveWmsCancelDto) =>
    wmsApi.post(ACCOUNTING_API.WMS_ORDER_RESOLVE_CANCEL, data),

  classify: (id: string) =>
    wmsApi.post(ACCOUNTING_API.WMS_ORDER_CLASSIFY(id)),

  stockCheck: (warehouseId: string, orderId: string) =>
    wmsApi.get(ACCOUNTING_API.WMS_ORDER_STOCK_CHECK(warehouseId, orderId)),

  stockCheckBatch: (warehouseId: string, orderIds?: string[]) => {
    let url = ACCOUNTING_API.WMS_ORDER_STOCK_CHECK_BATCH(warehouseId);
    if (orderIds && orderIds.length > 0) url += `?orderIds=${orderIds.join(',')}`;
    return wmsApi.get(url);
  },

  getSummary: (warehouseId: string) =>
    wmsApi.get(ACCOUNTING_API.WMS_ORDER_SUMMARY(warehouseId)),

  // Sprint 2 — Toplama kanal kartlari: Pending + InWave + Picking siparislerin kanal + tip kirilimi.
  getChannelSummary: (warehouseId: string) =>
    wmsApi.get(ACCOUNTING_API.WMS_ORDER_CHANNEL_SUMMARY(warehouseId)),

  getWaveCandidates: (warehouseId: string, orderType?: string, limit?: number, channel?: string) => {
    let url = ACCOUNTING_API.WMS_ORDER_WAVE_CANDIDATES(warehouseId);
    const qp = new URLSearchParams();
    if (orderType) qp.append('orderType', orderType);
    if (channel) qp.append('channel', channel);
    if (limit) qp.append('limit', limit.toString());
    const qs = qp.toString();
    if (qs) url += `?${qs}`;
    return wmsApi.get(url);
  },

  assignPersonnel: (data: AssignOrderPersonnelDto) =>
    wmsApi.post(ACCOUNTING_API.WMS_ORDER_ASSIGN_PERSONNEL, data),

  unassignPersonnel: (data: UnassignOrderPersonnelDto) =>
    wmsApi.post(ACCOUNTING_API.WMS_ORDER_UNASSIGN_PERSONNEL, data),

  getByPersonnel: (warehouseId: string, personnelId: string) =>
    wmsApi.get(ACCOUNTING_API.WMS_ORDER_BY_PERSONNEL, { params: { warehouseId, personnelId } }),
};
